<template>
  <n-modal
    v-model:show="isVisible"
    preset="card"
    :title="`Pedido #${orderData?.VBELN || orderData?.CABOUT?.VBELN || orderData?.PEDIDO || 'N/A'}`"
    size="large"
    :style="{ maxWidth: '64rem', maxHeight: '90vh' }"
    :mask-closable="true"
    :close-on-esc="true"
  >
    <n-spin :show="loading">
      <div v-if="!loading && orderData" class="flex flex-col">
        <div class="flex items-center gap-6 mb-4">
          <Logo
            v-if="orderData.VKORG || orderData.CABOUT?.VKORG"
            :brand="getBrandFromVKORG(orderData.VKORG || orderData.CABOUT?.VKORG)"
            size="md"
          />
          <n-tag
            v-if="orderData.ESTADO || orderData.CABOUT?.ESTADO"
            :type="getStatusType(orderData.ESTADO || orderData.CABOUT?.ESTADO)"
          >
            {{ orderData.ESTADO || orderData.CABOUT?.ESTADO }}
          </n-tag>
        </div>

        <!-- Resumen superior -->
        <div class="mb-4 bg-gray-50 p-3 rounded-md flex justify-between items-center text-sm">
          <div class="flex items-center gap-4">
            <div v-if="orderData.REF_PEDIDO">
              <div class="text-gray-600 font-medium">Referencia</div>
              <div class="font-bold">{{ orderData.REF_PEDIDO }}</div>
            </div>
            <div v-if="orderData.CABOUT?.PRECIO || orderData.PRECIO">
              <div class="text-gray-600 font-medium">Precio</div>
              <div class="font-bold">{{ replaceCurrencyCode(orderData.CABOUT?.PRECIO || orderData.PRECIO) }}</div>
            </div>
            <div
              v-if="
                orderData.CABOUT?.ZFECHA_GRA || orderData.CABIN?.FECHA_PDO || orderData.FECHA_REC
              "
            >
              <div class="text-gray-600 font-medium">Fecha pedido</div>
              <div class="font-bold">
                {{
                  formatDate(
                    orderData.CABOUT?.ZFECHA_GRA ||
                      orderData.CABIN?.FECHA_PDO ||
                      orderData.FECHA_REC
                  )
                }}
              </div>
            </div>
          </div>
          <div class="flex items-center gap-4">
            <div v-if="orderData.CABOUT?.FP_LLEGADA_CTE || orderData.FP_LLEGADA_CTE">
              <div class="text-gray-600 font-medium">Entrega prevista</div>
              <div class="font-bold" :class="{ 'text-red-600': isDelayed(orderData) }">
                {{ formatDate(orderData.CABOUT?.FP_LLEGADA_CTE || orderData.FP_LLEGADA_CTE) }}
              </div>
            </div>
            <n-tag v-if="isDelayed(orderData)" type="error" size="small"> DEMORADO </n-tag>
          </div>
        </div>

        <!-- Pestañas -->
        <n-tabs v-model:value="activeTab" type="line" animated>
          <n-tab-pane name="general" tab="Información General">
            <div class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div class="bg-gray-50 p-3 rounded-md">
                  <h3 class="font-bold text-lg mb-2 pb-2 border-b border-gray-200">
                    Información General
                  </h3>
                  <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div v-if="orderData.CABIN?.REF_CLIENT || orderData.REF_CLIENTE">
                      <div class="text-gray-600 font-medium">Destino</div>
                      <div class="text-gray-900">
                        {{ orderData.CABIN?.REF_CLIENT || orderData.REF_CLIENTE }}
                      </div>
                    </div>
                    <div v-if="orderData.CABOUT?.CENTRO_PED || orderData.CENTRO">
                      <div class="text-gray-600 font-medium">Centro</div>
                      <div class="text-gray-900">
                        {{ orderData.CABOUT?.CENTRO_PED || orderData.CENTRO }}
                      </div>
                    </div>
                    <div v-if="orderData.CABIN?.AUGRU || orderData.TIPO">
                      <div class="text-gray-600 font-medium">Tipo</div>
                      <div class="text-gray-900">
                        {{ orderData.CABIN?.AUGRU || orderData.TIPO }}
                      </div>
                    </div>
                    <div v-if="orderData.RECEPTOR">
                      <div class="text-gray-600 font-medium">Receptor</div>
                      <div class="text-gray-900">{{ orderData.RECEPTOR }}</div>
                    </div>
                    <div
                      v-if="
                        orderData.CABOUT?.ZHORA_GRA ||
                        orderData.CABIN?.HORA_PDO ||
                        orderData.HORA_REC
                      "
                    >
                      <div class="text-gray-600 font-medium">Hora creación</div>
                      <div class="text-gray-900">
                        {{
                          orderData.CABOUT?.ZHORA_GRA ||
                          orderData.CABIN?.HORA_PDO ||
                          orderData.HORA_REC
                        }}
                      </div>
                    </div>
                    <div v-if="orderData.ZSTK_FAB">
                      <div class="text-gray-600 font-medium">Tipo Fabricación</div>
                      <div class="text-gray-900">
                        {{ orderData.ZSTK_FAB === "F" ? "Fabricación" : "Stock" }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="bg-gray-50 p-3 rounded-md">
                  <h3 class="font-bold text-lg mb-2 pb-2 border-b border-gray-200">
                    Información de Envío
                  </h3>
                  <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div v-if="orderData.CABOUT?.FECHA_SAL || orderData.FECHA_SAL">
                      <div class="text-gray-600 font-medium">Fecha salida</div>
                      <div class="text-gray-900">
                        {{ formatDate(orderData.CABOUT?.FECHA_SAL || orderData.FECHA_SAL) }}
                      </div>
                    </div>
                    <div v-if="orderData.FP_REPARTO_CTE">
                      <div class="text-gray-600 font-medium">Reparto previsto</div>
                      <div class="text-gray-900">{{ formatDate(orderData.FP_REPARTO_CTE) }}</div>
                    </div>
                    <div v-if="orderData.ENTREGA">
                      <div class="text-gray-600 font-medium">Entrega</div>
                      <div class="text-gray-900">{{ orderData.ENTREGA }}</div>
                    </div>
                    <div v-if="orderData.TRANSPORTISTA">
                      <div class="text-gray-600 font-medium">Transportista</div>
                      <div class="text-gray-900">{{ orderData.TRANSPORTISTA }}</div>
                    </div>
                    <div v-if="orderData.TRACKING" class="col-span-2">
                      <div class="text-gray-600 font-medium">Tracking</div>
                      <div class="flex items-center gap-2 text-gray-900">
                        {{ orderData.TRACKING }}
                        <a
                          v-if="orderData.url_tracking"
                          :href="orderData.url_tracking"
                          target="_blank"
                          class="text-blue-600"
                        >
                          <Icon name="info" size="sm" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="additionalDetails && additionalDetails.length > 0"
                class="bg-gray-50 p-3 rounded-md"
              >
                <h3 class="font-bold text-lg mb-2 pb-2 border-b border-gray-200">
                  Detalles Adicionales
                </h3>
                <div class="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
                  <div v-for="(detail, index) in additionalDetails" :key="index">
                    <div class="text-gray-600 font-medium">{{ detail.label }}</div>
                    <div class="text-gray-900">{{ detail.value }}</div>
                  </div>
                </div>
              </div>
            </div>
          </n-tab-pane>

          <n-tab-pane name="productos" tab="Productos">
            <div class="bg-gray-50 p-3 rounded-md">
              <div class="grid grid-cols-2 gap-8">
                <!-- Ojo Derecho -->
                <div v-if="orderData.ojoOutDerecho || orderData.PROD_OD">
                  <div class="flex items-center gap-2 mb-3">
                    <div class="w-2 h-2 rounded-full bg-blue-600"></div>
                    <h3 class="font-bold text-lg text-blue-600">Ojo Derecho</h3>
                  </div>
                  <div class="mb-4 pb-2 border-b border-gray-200">
                    <div class="text-sm text-gray-600 font-medium">Producto</div>
                    <div class="font-medium text-gray-900">
                      {{ orderData.ojoOutDerecho?.TXT_LENT || orderData.PROD_OD }}
                    </div>
                  </div>

                  <div class="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div v-if="orderData.ojoOutDerecho?.FOCO || orderData.FOCO_OD">
                      <div class="text-gray-600 font-medium">Foco</div>
                      <div class="text-gray-900">
                        {{ getFocoText(orderData.ojoOutDerecho?.FOCO || orderData.FOCO_OD) }}
                      </div>
                    </div>
                    <div v-if="orderData.ojoOutDerecho?.Z_DIA_LEN || orderData.Z_DIA_LEN_OD">
                      <div class="text-gray-600 font-medium">Diámetro</div>
                      <div class="text-gray-900">
                        {{ orderData.ojoOutDerecho?.Z_DIA_LEN || orderData.Z_DIA_LEN_OD }}
                      </div>
                    </div>
                    <div>
                      <div class="text-gray-600 font-medium">Tratamientos</div>
                      <div class="text-gray-900">
                        {{
                          (orderData.ojoOutDerecho?.TIENE_TRAT || orderData.TIENE_TRAT_OD) === "X"
                            ? "Sí"
                            : "No"
                        }}
                      </div>
                    </div>
                    <div v-if="orderData.ojoOutDerecho?.ESTADO || orderData.ESTADO_OD">
                      <div class="text-gray-600 font-medium">Estado</div>
                      <div class="text-gray-900">
                        {{
                          getFabricationStatus(
                            orderData.ojoOutDerecho?.ESTADO || orderData.ESTADO_OD
                          )
                        }}
                      </div>
                    </div>
                  </div>

                  <div class="bg-gray-100 p-3 rounded-md">
                    <h4 class="font-semibold mb-2 text-sm">Graduación</h4>
                    <div class="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div class="text-gray-600 font-medium">Esfera</div>
                        <div class="font-medium text-gray-900">
                          {{
                            formatPrescription(
                              orderData.ojoOutDerecho?.Z_SIGNO_ES || orderData.Z_SIGNO_ES_OD,
                              orderData.ojoOutDerecho?.Z_ESFERA || orderData.Z_ESFERA_OD
                            )
                          }}
                        </div>
                      </div>
                      <div>
                        <div class="text-gray-600 font-medium">Cilindro</div>
                        <div class="font-medium text-gray-900">
                          {{
                            formatPrescription(
                              orderData.ojoOutDerecho?.Z_SIGNO_CL || orderData.Z_SIGNO_CL_OD,
                              orderData.ojoOutDerecho?.Z_CILINDRO || orderData.Z_CILINDRO_OD
                            )
                          }}
                        </div>
                      </div>
                      <div>
                        <div class="text-gray-600 font-medium">Eje</div>
                        <div class="font-medium text-gray-900">
                          {{ orderData.ojoOutDerecho?.ZEJE || orderData.ZEJE_OD }}
                        </div>
                      </div>
                      <div>
                        <div class="text-gray-600 font-medium">Adición</div>
                        <div class="font-medium text-gray-900">
                          {{ orderData.ojoOutDerecho?.ZADICION || orderData.ZADICION_OD }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Ojo Izquierdo -->
                <div v-if="orderData.ojoOutIzquierdo || orderData.PROD_OI">
                  <div class="flex items-center gap-2 mb-3">
                    <div class="w-2 h-2 rounded-full bg-gray-700"></div>
                    <h3 class="font-bold text-lg text-gray-700">Ojo Izquierdo</h3>
                  </div>
                  <div class="mb-4 pb-2 border-b border-gray-200">
                    <div class="text-sm text-gray-600 font-medium">Producto</div>
                    <div class="font-medium text-gray-900">
                      {{ orderData.ojoOutIzquierdo?.TXT_LENT || orderData.PROD_OI }}
                    </div>
                  </div>

                  <div class="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div v-if="orderData.ojoOutIzquierdo?.FOCO || orderData.FOCO_OI">
                      <div class="text-gray-600 font-medium">Foco</div>
                      <div class="text-gray-900">
                        {{ getFocoText(orderData.ojoOutIzquierdo?.FOCO || orderData.FOCO_OI) }}
                      </div>
                    </div>
                    <div v-if="orderData.ojoOutIzquierdo?.Z_DIA_LEN || orderData.Z_DIA_LEN_OI">
                      <div class="text-gray-600 font-medium">Diámetro</div>
                      <div class="text-gray-900">
                        {{ orderData.ojoOutIzquierdo?.Z_DIA_LEN || orderData.Z_DIA_LEN_OI }}
                      </div>
                    </div>
                    <div>
                      <div class="text-gray-600 font-medium">Tratamientos</div>
                      <div class="text-gray-900">
                        {{
                          (orderData.ojoOutIzquierdo?.TIENE_TRAT || orderData.TIENE_TRAT_OI) === "X"
                            ? "Sí"
                            : "No"
                        }}
                      </div>
                    </div>
                    <div v-if="orderData.ojoOutIzquierdo?.ESTADO || orderData.ESTADO_OI">
                      <div class="text-gray-600 font-medium">Estado</div>
                      <div class="text-gray-900">
                        {{
                          getFabricationStatus(
                            orderData.ojoOutIzquierdo?.ESTADO || orderData.ESTADO_OI
                          )
                        }}
                      </div>
                    </div>
                  </div>

                  <div class="bg-gray-100 p-3 rounded-md">
                    <h4 class="font-semibold mb-2 text-sm">Graduación</h4>
                    <div class="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div class="text-gray-600 font-medium">Esfera</div>
                        <div class="font-medium text-gray-900">
                          {{
                            formatPrescription(
                              orderData.ojoOutIzquierdo?.Z_SIGNO_ES || orderData.Z_SIGNO_ES_OI,
                              orderData.ojoOutIzquierdo?.Z_ESFERA || orderData.Z_ESFERA_OI
                            )
                          }}
                        </div>
                      </div>
                      <div>
                        <div class="text-gray-600 font-medium">Cilindro</div>
                        <div class="font-medium text-gray-900">
                          {{
                            formatPrescription(
                              orderData.ojoOutIzquierdo?.Z_SIGNO_CL || orderData.Z_SIGNO_CL_OI,
                              orderData.ojoOutIzquierdo?.Z_CILINDRO || orderData.Z_CILINDRO_OI
                            )
                          }}
                        </div>
                      </div>
                      <div>
                        <div class="text-gray-600 font-medium">Eje</div>
                        <div class="font-medium text-gray-900">
                          {{ orderData.ojoOutIzquierdo?.ZEJE || orderData.ZEJE_OI }}
                        </div>
                      </div>
                      <div>
                        <div class="text-gray-600 font-medium">Adición</div>
                        <div class="font-medium text-gray-900">
                          {{ orderData.ojoOutIzquierdo?.ZADICION || orderData.ZADICION_OI }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </n-tab-pane>

          <n-tab-pane name="seguimiento" tab="Estado y Seguimiento">
            <div class="space-y-4">
              <div class="bg-gray-50 p-3 rounded-md">
                <h3 class="font-bold text-lg mb-3 pb-2 border-b border-gray-200">
                  Seguimiento Temporal
                </h3>

                <div class="relative pl-8">
                  <div class="absolute top-0 bottom-0 left-4 w-0.5 bg-gray-300"></div>

                  <!-- Pedido realizado -->
                  <div class="relative mb-4">
                    <div
                      class="absolute left-[-1.75rem] top-0 w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center"
                    >
                      <Icon name="info" class="text-white" size="xs" />
                    </div>
                    <div class="font-medium text-blue-600">Pedido realizado</div>
                    <div class="text-sm text-gray-700">
                      {{
                        formatDate(
                          orderData.CABOUT?.ZFECHA_GRA ||
                            orderData.CABIN?.FECHA_PDO ||
                            orderData.FECHA_REC
                        )
                      }}
                      {{
                        orderData.CABOUT?.ZHORA_GRA ||
                        orderData.CABIN?.HORA_PDO ||
                        orderData.HORA_REC ||
                        ""
                      }}
                    </div>
                  </div>

                  <!-- Fabricación -->
                  <div
                    v-if="
                      orderData.ESTADO_OD ||
                      orderData.ESTADO_OI ||
                      orderData.ojoOutDerecho?.ESTADO ||
                      orderData.ojoOutIzquierdo?.ESTADO
                    "
                    class="relative mb-4"
                  >
                    <div
                      class="absolute left-[-1.75rem] top-0 w-5 h-5 rounded-full"
                      :class="
                        isStepComplete(
                          ['C'],
                          orderData.ESTADO_OD || orderData.ojoOutDerecho?.ESTADO,
                          orderData.ESTADO_OI || orderData.ojoOutIzquierdo?.ESTADO
                        )
                          ? 'bg-blue-600'
                          : 'bg-gray-200 border border-gray-300'
                      "
                    >
                      <Icon
                        v-if="
                          isStepComplete(
                            ['C'],
                            orderData.ESTADO_OD || orderData.ojoOutDerecho?.ESTADO,
                            orderData.ESTADO_OI || orderData.ojoOutIzquierdo?.ESTADO
                          )
                        "
                        name="info"
                        class="text-white"
                        size="xs"
                      />
                    </div>
                    <div
                      class="font-medium"
                      :class="
                        isStepComplete(
                          ['C'],
                          orderData.ESTADO_OD || orderData.ojoOutDerecho?.ESTADO,
                          orderData.ESTADO_OI || orderData.ojoOutIzquierdo?.ESTADO
                        )
                          ? 'text-blue-600'
                          : 'text-gray-700'
                      "
                    >
                      Fabricación
                    </div>
                    <div class="text-sm text-gray-700 flex flex-col gap-1 mt-1">
                      <div>
                        Ojo Derecho:
                        <span class="font-semibold">{{
                          getFabricationStatus(
                            orderData.ESTADO_OD ||
                              orderData.ojoOutDerecho?.ESTADO ||
                              orderData.ESTADO_OI ||
                              orderData.ojoOutIzquierdo?.ESTADO
                          )
                        }}</span>
                      </div>
                      <div>
                        Ojo Izquierdo:
                        <span class="font-semibold">{{
                          getFabricationStatus(
                            orderData.ESTADO_OI || orderData.ojoOutIzquierdo?.ESTADO
                          )
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Fecha de salida -->
                  <div
                    v-if="orderData.CABOUT?.FECHA_SAL || orderData.FECHA_SAL"
                    class="relative mb-4"
                  >
                    <div
                      class="absolute left-[-1.75rem] top-0 w-5 h-5 rounded-full"
                      :class="
                        isDateInPast(orderData.CABOUT?.FECHA_SAL || orderData.FECHA_SAL)
                          ? 'bg-blue-600'
                          : 'bg-gray-200 border border-gray-300'
                      "
                    >
                      <Icon
                        v-if="isDateInPast(orderData.CABOUT?.FECHA_SAL || orderData.FECHA_SAL)"
                        name="info"
                        class="text-white"
                        size="xs"
                      />
                    </div>
                    <div
                      class="font-medium"
                      :class="
                        isDateInPast(orderData.CABOUT?.FECHA_SAL || orderData.FECHA_SAL)
                          ? 'text-blue-600'
                          : 'text-gray-700'
                      "
                    >
                      Salida del centro
                    </div>
                    <div class="text-sm text-gray-700">
                      {{ formatDate(orderData.CABOUT?.FECHA_SAL || orderData.FECHA_SAL) }}
                    </div>
                  </div>

                  <!-- Fecha prevista de reparto -->
                  <div v-if="orderData.FP_REPARTO_CTE" class="relative mb-4">
                    <div
                      class="absolute left-[-1.75rem] top-0 w-5 h-5 rounded-full"
                      :class="
                        isDateInPast(orderData.FP_REPARTO_CTE)
                          ? 'bg-blue-600'
                          : 'bg-gray-200 border border-gray-300'
                      "
                    >
                      <Icon
                        v-if="isDateInPast(orderData.FP_REPARTO_CTE)"
                        name="info"
                        class="text-white"
                        size="xs"
                      />
                    </div>
                    <div
                      class="font-medium"
                      :class="
                        isDateInPast(orderData.FP_REPARTO_CTE) ? 'text-blue-600' : 'text-gray-700'
                      "
                    >
                      Reparto
                      <a
                        v-if="orderData.ENTREGA"
                        :href="`https://www.correos.es/es/es/herramientas/localizador/envios/detalle?tracking-number=${orderData.ENTREGA}`"
                        target="_blank"
                        class="inline-flex items-center ml-2"
                      >
                        <Icon name="delivery-truck-pin" size="sm" class="text-blue-600" />
                      </a>
                    </div>
                    <div class="text-sm text-gray-700">
                      Previsto: {{ formatDate(orderData.FP_REPARTO_CTE) }}
                    </div>
                  </div>

                  <!-- Fecha prevista de llegada -->
                  <div
                    v-if="orderData.CABOUT?.FP_LLEGADA_CTE || orderData.FP_LLEGADA_CTE"
                    class="relative mb-4"
                  >
                    <div
                      class="absolute left-[-1.75rem] top-0 w-5 h-5 rounded-full"
                      :class="
                        isDateInPast(orderData.CABOUT?.FP_LLEGADA_CTE || orderData.FP_LLEGADA_CTE)
                          ? 'bg-blue-600'
                          : 'bg-gray-200 border border-gray-300'
                      "
                    >
                      <Icon
                        v-if="
                          isDateInPast(orderData.CABOUT?.FP_LLEGADA_CTE || orderData.FP_LLEGADA_CTE)
                        "
                        name="info"
                        class="text-white"
                        size="xs"
                      />
                    </div>
                    <div
                      class="font-medium"
                      :class="
                        isDateInPast(orderData.CABOUT?.FP_LLEGADA_CTE || orderData.FP_LLEGADA_CTE)
                          ? 'text-blue-600'
                          : 'text-gray-700'
                      "
                    >
                      Entrega al cliente
                    </div>
                    <div
                      class="text-sm"
                      :class="isDelayed(orderData) ? 'text-red-600' : 'text-gray-700'"
                    >
                      <span class="font-semibold">Prevista:</span>
                      {{ formatDate(orderData.CABOUT?.FP_LLEGADA_CTE || orderData.FP_LLEGADA_CTE) }}
                      <span v-if="isDelayed(orderData)" class="font-semibold text-red-600 ml-2"
                        >(DEMORADO)</span
                      >
                    </div>
                    <div
                      v-if="
                        orderData.FR_LLEGADA_CTE &&
                        orderData.FR_LLEGADA_CTE !== '0001-01-01T00:00:00'
                      "
                      class="text-sm text-gray-700"
                    >
                      <span class="font-semibold">Real:</span>
                      {{ formatDate(orderData.FR_LLEGADA_CTE) }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="orderData.TRACKING || orderData.TRANSPORTISTA"
                class="bg-gray-50 p-3 rounded-md"
              >
                <h3 class="font-bold text-lg mb-2 pb-2 border-b border-gray-200">
                  Información de Transporte
                </h3>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div v-if="orderData.TRANSPORTISTA">
                    <div class="text-gray-600 font-medium">Transportista</div>
                    <div class="text-gray-900 font-semibold">{{ orderData.TRANSPORTISTA }}</div>
                  </div>
                  <div v-if="orderData.ENTREGA">
                    <div class="text-gray-600 font-medium">Número de entrega</div>
                    <div class="text-gray-900 font-semibold">{{ orderData.ENTREGA }}</div>
                  </div>
                  <div v-if="orderData.TRACKING" class="col-span-2">
                    <div class="text-gray-600 font-medium">Tracking</div>
                    <div class="flex items-center gap-2">
                      <span class="text-gray-900 font-semibold">{{ orderData.TRACKING }}</span>
                      <a
                        v-if="orderData.url_tracking"
                        :href="orderData.url_tracking"
                        target="_blank"
                        class="text-blue-600 underline font-medium"
                      >
                        Seguir envío
                      </a>
                      <a
                        v-else-if="orderData.ENTREGA"
                        :href="`https://www.correos.es/es/es/herramientas/localizador/envios/detalle?tracking-number=${orderData.ENTREGA}`"
                        target="_blank"
                        class="text-blue-600 underline font-medium"
                      >
                        Seguir envío
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </n-tab-pane>
        </n-tabs>
      </div>
    </n-spin>

    <div v-if="!loading && !orderData" class="p-6 text-center">
      <div class="text-xl text-red-600 mb-4">Error al cargar los detalles</div>
      <p class="mb-6">
        No se pudieron obtener los detalles del pedido. Por favor, inténtelo de nuevo más tarde.
      </p>
      <n-button type="primary" @click="closeModal">Cerrar</n-button>
    </div>
  </n-modal>
</template>

<script>
import { NModal, NSpin, NTabs, NTabPane, NTag } from "naive-ui";
import Icon from "@/components/theme/ui/Icon.vue";
import Logo from "@/components/theme/ui/Logo.vue";
import { replaceCurrencyCode } from "@/helpers/currency";

export default {
  name: "OrderSearchModal",
  components: {
    NModal,
    NSpin,
    NTabs,
    NTabPane,
    NTag,
    Icon,
    Logo,
  },
  data() {
    return {
      isVisible: false,
      loading: false,
      orderData: null,
      additionalDetails: [],
      activeTab: "general",
    };
  },
  methods: {
    replaceCurrencyCode,
    async show(orderData) {
      this.orderData = orderData;
      this.additionalDetails = [];
      this.activeTab = "general";
      this.processAdditionalDetails();
      this.isVisible = true;
    },

    closeModal() {
      this.isVisible = false;
    },

    processAdditionalDetails() {
      if (!this.orderData) return;

      const details = [];

      if (this.orderData.FACTURA) {
        details.push({ label: "Factura", value: this.orderData.FACTURA });
      }

      if (this.orderData.FORMA_PAGO) {
        details.push({ label: "Forma de Pago", value: this.orderData.FORMA_PAGO });
      }

      if (this.orderData.OBSERVACIONES) {
        details.push({ label: "Observaciones", value: this.orderData.OBSERVACIONES });
      }

      if (this.orderData.ANO) {
        details.push({ label: "Año", value: this.orderData.ANO.toString() });
      }

      this.additionalDetails = details;
    },

    formatDate(date) {
      if (!date || date.split("T")[0] === "0001-01-01") return "";
      if (typeof date === "string" && date.includes("T")) {
        const [year, month, day] = date.split("T")[0].split("-");
        return `${day}.${month}.${year}`;
      }
      return date;
    },

    isDateInPast(dateStr) {
      if (!dateStr || dateStr === "0001-01-01T00:00:00") return false;
      const date = new Date(dateStr);
      return date <= new Date();
    },

    isStepComplete(completeStates, ...states) {
      return states.every((state) => completeStates.includes(state));
    },

    formatPrescription(sign, value) {
      if (!value) return "";
      return `${sign === "-" ? "-" : "+"}${value}`;
    },

    getFocoText(foco) {
      const focoMap = {
        P: "Progresivo",
        M: "Monofocal",
        B: "Bifocal",
        O: "Ocupacional",
      };
      return focoMap[foco] || foco;
    },

    getFabricationStatus(status) {
      if (!status) return "N/A";
      const statusMap = {
        C: "Completado",
        P: "Pendiente",
        E: "En proceso",
        R: "Rechazado",
      };
      return statusMap[status] || status;
    },

    getBrandFromVKORG(vkorg) {
      const brandMap = {
        "0803": "indo",
        "0808": "rodenstock",
        "0809": "optovision",
      };
      return brandMap[vkorg] || "indo";
    },

    getStatusType(status) {
      const typeMap = {
        "En curso": "success",
        "Enviado a revisión": "warning",
        Expedido: "info",
        Procesado: "info",
        Aceptado: "success",
      };
      return typeMap[status] || "default";
    },

    isDelayed(pedido) {
      // Comparar fecha prevista con fecha actual
      const fechaPrevista = pedido.CABOUT?.FP_LLEGADA_CTE || pedido.FP_LLEGADA_CTE;
      if (!fechaPrevista) return false;

      const fechaPrevistaDate = new Date(fechaPrevista);
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      return fechaPrevistaDate < hoy && !pedido.FR_LLEGADA_CTE;
    },
  },
};
</script>

<style scoped>
/* Estilos adicionales si son necesarios */
</style>
